import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { daysUntilCalendarDate } from '../../../lib/calendar-date';
import { useAuth } from '../../../contexts/AuthContext';
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Loader2,
  ChevronRight,
  CalendarOff,
  LogOut,
} from 'lucide-react';
import { useBack } from '../../../hooks/useBack';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { useMyHos } from '../../../hooks/useMyHos';
import { queryLoadFailure } from '../../../lib/load-failure';
import { fmtHours } from '../../../lib/display-labels';

export default function DriverProfileScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/driver');
  const { user, logout } = useAuth();
  // Cert expiry counts from the TENANT's today, not the phone's.
  const timeZone = useTenantTimeZone();

  // Fetch driver profile from API
  const { data: driverData, isLoading } = useQuery({
    queryKey: ['driver-profile'],
    queryFn: () =>
      apiClient
        .get('/trucking/drivers/me')
        .then((r) => r.data?.data ?? r.data ?? null),
    retry: false,
  });

  /*
   * HOURS OF SERVICE, as GET /trucking/drivers/me/hos sends them (#815). The
   * recap used to read `driving_today`, `on_duty_today` and `cycle_hours`, keys
   * the route has never sent, and drew "0 / 13h" for everyone. The route sends
   * 7-DAY figures only, so there are no "today" rows: nothing sends a per-day
   * figure. Since the API fix a driver with no logs in the window reads null,
   * which is a dash here; a driver who logged and drove nothing reads real 0s.
   */
  const hosQuery = useMyHos();
  const hos = hosQuery.data ?? null;
  const hosFailed = queryLoadFailure(hosQuery) != null;
  const hoursFig = (v: number | null | undefined) => (hosFailed || !hos ? '—' : v == null ? '—' : `${fmtHours(v, 1)}h`);

  // Merge API data with auth user fallback
  const driver = {
    firstName: driverData?.first_name || user?.firstName || 'Driver',
    lastName: driverData?.last_name || user?.lastName || '',
    email: driverData?.email || user?.email || '',
    phone: driverData?.phone || user?.phoneNumber || '',
    // `position_name` is what /trucking/drivers/me sends. The old default,
    // "Float Operator", put a job title on every driver the office never gave one.
    position: driverData?.position_name || '—',
    truck: driverData?.fleet_unit_number || driverData?.truck_unit || driverData?.assigned_truck?.unit_number || 'Unassigned',
    truckType: driverData?.fleet_make && driverData?.fleet_model
      ? `${driverData.fleet_year ? driverData.fleet_year + ' ' : ''}${driverData.fleet_make} ${driverData.fleet_model}`
      : '',
    licenseClass: driverData?.license_class || '—',
    licenseNumber: driverData?.license_number || '',
    licenseExpiry: driverData?.license_expiry || '',
    certifications: driverData?.certifications || [],
    payMethod: driverData?.pay_method || '',
  };

  const initials =
    (driver.firstName?.[0] || '') + (driver.lastName?.[0] || '') || 'D';

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [language, setLanguage] = useState('en');

  /**
   * NO EXPIRY IS NOT "VALID".
   *
   * `daysUntilCalendarDate` returns null for an absent date AND for one it
   * cannot parse, and both fell to 'current' — so a certification row the
   * office has never dated, or dated in a format this build cannot read,
   * rendered a green **Valid** pill next to the cert's name. The driver reads
   * that on their own phone before a shift.
   *
   * 'unknown' is the fourth state, and it is deliberately not red: a missing
   * date is not evidence the cert has lapsed, only evidence that nobody can
   * tell from here. Slate, with the label saying which of the two it is.
   *
   * The same distinction fe#702 drew on the team screen: an empty set is a
   * fact about the person, an absent value is a fact about the data, and the
   * two must not share a spelling.
   */
  function getCertStatus(expiry: string) {
    const daysUntil = daysUntilCalendarDate(expiry, timeZone);
    if (daysUntil == null) return 'unknown';
    if (daysUntil < 0) return 'expired';
    if (daysUntil < 60) return 'expiring';
    return 'current';
  }

  const certStatusColors: Record<string, string> = {
    current: 'bg-green-500/20 text-green-400',
    expiring: 'bg-amber-500/20 text-amber-400',
    expired: 'bg-red-500/20 text-red-400',
    unknown: 'bg-fieldiq-border/40 text-fieldiq-muted',
  };

  const certStatusLabels: Record<string, string> = {
    current: 'Valid',
    expiring: 'Renew Soon',
    expired: 'Expired',
    unknown: 'No expiry on file',
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      {/* Header */}
      <div
        className="bg-fieldiq-dark border-b border-fieldiq-border px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button onClick={goBack} className="text-fieldiq-muted">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-base font-bold text-white">My Profile</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        ) : (
          <>
            {/* Profile Header Card */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-5 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-700/40 flex items-center justify-center text-amber-300 font-bold text-xl mx-auto mb-3">
                {initials}
              </div>
              <h2 className="text-lg font-bold text-white">
                {driver.firstName} {driver.lastName}
              </h2>
              <p className="text-sm text-fieldiq-muted">
                {driver.position} &middot; {driver.truck}{driver.truckType ? ` (${driver.truckType})` : ''}
              </p>
            </div>

            {/* Quick Links */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl overflow-hidden">
              {/* /field/pay and /field/time-off are the driver's pay and time-off
                * screens. The /driver/* paths these used are not routed and bounced
                * to today (#815). */}
              <button
                onClick={() => navigate('/field/pay')}
                className="w-full flex items-center gap-3 px-4 py-3.5 border-b border-fieldiq-border"
              >
                <DollarSign size={18} className="text-fieldiq-accent" />
                <span className="flex-1 text-sm font-medium text-white text-left">My Pay</span>
                <ChevronRight size={16} className="text-fieldiq-muted" />
              </button>
              <button
                onClick={() => navigate('/field/time-off')}
                className="w-full flex items-center gap-3 px-4 py-3.5"
              >
                <CalendarOff size={18} className="text-fieldiq-accent" />
                <span className="flex-1 text-sm font-medium text-white text-left">Time Off</span>
                <ChevronRight size={16} className="text-fieldiq-muted" />
              </button>
            </div>

            {/* Licence & Certs */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
                Licence &amp; Certifications
              </h3>

              <div className="bg-fieldiq-dark border border-fieldiq-border rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-fieldiq-muted">Class</span>
                  <span className="text-white font-medium">{driver.licenseClass}</span>
                </div>
                {driver.licenseNumber && (
                  <div className="flex justify-between text-xs">
                    <span className="text-fieldiq-muted">Number</span>
                    <span className="text-white font-medium">{driver.licenseNumber}</span>
                  </div>
                )}
                {driver.licenseExpiry && (
                  <div className="flex justify-between text-xs">
                    <span className="text-fieldiq-muted">Expiry</span>
                    <span className="text-white font-medium">{driver.licenseExpiry}</span>
                  </div>
                )}
              </div>

              {driver.certifications.length > 0 && (
                <div className="space-y-2">
                  {driver.certifications.map((cert: any) => {
                    const status = getCertStatus(cert.expiry || cert.expiry_date);
                    return (
                      <div
                        key={cert.name || cert.cert_name}
                        className="flex items-center justify-between bg-fieldiq-dark border border-fieldiq-border rounded-lg p-3"
                      >
                        <div>
                          <p className="text-sm text-white font-medium">{cert.name || cert.cert_name}</p>
                          {(cert.expiry || cert.expiry_date) && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Calendar size={10} className="text-fieldiq-muted" />
                              <span className="text-[10px] text-fieldiq-muted">
                                Exp: {cert.expiry || cert.expiry_date}
                              </span>
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            certStatusColors[status] || certStatusColors.unknown
                          }`}
                        >
                          {certStatusLabels[status]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* This Pay Period: the three tiles read hours_logged, estimated_pay and
              * next_pay_date, none of which /trucking/drivers/me sends, so they showed
              * "0", "$0" and "--" for everyone. The real figures are on the pay screen. */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
                Pay
              </h3>

              {driver.payMethod && (
                <p className="text-[10px] text-fieldiq-muted text-center">
                  Pay method: {driver.payMethod}
                </p>
              )}

              <button
                onClick={() => navigate('/field/pay')}
                className="flex items-center justify-center gap-1 w-full text-xs text-fieldiq-accent font-medium py-2 min-h-[48px]"
              >
                View full pay <ChevronRight size={14} />
              </button>
            </div>

            {/* HOS: the 7-day cycle, as sent. See the note at the top of the component. */}
            <div data-testid="driver-hos-recap" className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
                HOS Cycle Recap (7 days)
              </h3>
              {[
                { label: 'Driving, last 7 days', value: hoursFig(hos?.hours_by_status?.driving), of: hos?.cycle_limit },
                { label: 'Driving left in cycle', value: hoursFig(hos?.driving_remaining) },
                { label: 'On-duty left in cycle', value: hoursFig(hos?.on_duty_remaining) },
              ].map((h) => (
                <div key={h.label} data-testid="driver-hos-row" className="flex justify-between text-xs">
                  <span className="text-fieldiq-muted">{h.label}</span>
                  <span className="text-white font-medium">
                    {h.value}
                    {h.of != null && h.value !== '—' ? ` of ${h.of}h` : ''}
                  </span>
                </div>
              ))}
              <p className="text-[10px] text-fieldiq-muted">
                {hosQuery.isLoading
                  ? ''
                  : hosFailed
                    ? 'Hours of service could not be loaded.'
                    : hos && hos.hours_by_status == null
                      ? 'No hours-of-service logs in the last 7 days.'
                      : 'Rolling 7-day totals. Per-day hours are not recorded.'}
              </p>
            </div>

            {/* Settings */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
              <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider">
                Settings
              </h3>
              <div className="flex items-center justify-between min-h-[48px]">
                <div>
                  <p className="text-sm text-white font-medium">Push Notifications</p>
                  <p className="text-[10px] text-fieldiq-muted">Load assignments &amp; dispatch alerts</p>
                </div>
                <button
                  onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    notificationsEnabled ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      notificationsEnabled ? 'left-[22px]' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between min-h-[48px]">
                <div>
                  <p className="text-sm text-white font-medium">Language</p>
                  <p className="text-[10px] text-fieldiq-muted">App display language</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      language === 'en'
                        ? 'bg-fieldiq-accent/15 text-fieldiq-accent border border-fieldiq-accent/30'
                        : 'text-fieldiq-muted border border-fieldiq-border'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => setLanguage('fr')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                      language === 'fr'
                        ? 'bg-fieldiq-accent/15 text-fieldiq-accent border border-fieldiq-accent/30'
                        : 'text-fieldiq-muted border border-fieldiq-border'
                    }`}
                  >
                    Fran&ccedil;ais
                  </button>
                </div>
              </div>
            </div>

            {/* Sign Out */}
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-lg border border-red-400/30 text-red-400 text-sm font-medium"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </>
        )}
      </div>

    </div>
  );
}
